import { PayerCoverageCheckNetwork } from "@common-shared/config/coverage.config"
import type { INetworkStatusRule } from "@common-shared/types/responseData/commonResponseData.types"

import { NETWORK_STATUS_CONFIG } from "../config/networkStatus.config"
import { RawNetworkStatusRuleCsvRowDTO } from "../dto/rawNetworkStatusRuleCsvInput.dto"
import { NetworkStatusExportRowKey } from "../types/networkStatus.types"
import { CsvHelper } from "./csv.helper"

export class NetworkStatusRulesCsvHelper {
    /**
     * Parse CSV text (first row headers, then data). No version line.
     * Excludes empty lines and any line that starts with # (comment lines).
     */
    static parse(csvText: string): RawNetworkStatusRuleCsvRowDTO[] {
        return CsvHelper.parseHeaderMappedRows(csvText, {
            headerRowIndex: NETWORK_STATUS_CONFIG.csvHeaderRowIndex,
            headerToKey: NETWORK_STATUS_CONFIG.csvHeaderToKey,
            arrayKeys: NETWORK_STATUS_CONFIG.csvArrayRowKeys,
            dtoClass: RawNetworkStatusRuleCsvRowDTO,
            transformRawRow: (raw, index) => {
                const importDate = new Date().toISOString().slice(0, 10)

                return {
                    ...raw,
                    name:
                        raw.name || `Import ${importDate} - Rule #${index + 1}`,
                }
            },
            withIndex: true,
        })
    }

    /**
     * Map a single rule to a CSV row in upload-compatible column order.
     * Same columns and order as upload template so downloaded CSV can be used for bulk create.
     * Network Status column uses enum values "IN" / "OUT" (accepted by upload parser and API).
     */
    static ruleToCsvRow(rule: INetworkStatusRule): string[] {
        const networkStatusValue =
            rule.networkStatus === PayerCoverageCheckNetwork.IN
                ? PayerCoverageCheckNetwork.IN
                : rule.networkStatus === PayerCoverageCheckNetwork.OUT
                  ? PayerCoverageCheckNetwork.OUT
                  : String(rule.networkStatus ?? "")

        const valueByKey: Record<NetworkStatusExportRowKey, string> = {
            networkStatus: networkStatusValue,
            payerId: rule.payer?.payerId ?? "",
            providerNpi: rule.providerNpi,
            startDate: rule.startDate ?? "",
            endDate: rule.endDate ?? "",
            thirdPartyAdministratorId:
                rule.thirdPartyAdministrator?.payerId ?? "",
            insuranceTypes: rule.insuranceTypes?.join(", ") ?? "",
            planTypes: rule.planTypes?.join(", ") ?? "",
            planName: rule.planName ?? "",
            groupName: rule.groupName ?? "",
            state: rule.state ?? "",
            name: rule.name ?? "",
            id: rule.id ? String(rule.id) : "",
        }

        return Object.keys(NETWORK_STATUS_CONFIG.csvExportHeadersToKey).map(
            header => {
                const valueKey: NetworkStatusExportRowKey | undefined =
                    NETWORK_STATUS_CONFIG.csvExportHeadersToKey[header]

                return valueKey ? (valueByKey[valueKey] ?? "") : ""
            },
        )
    }
}
